import { useEffect, useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertCircle } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@quebon/ui/components/dialog'
import { memberApi } from '@/api/b2b'

interface Props {
  target: { userId: number; fullName: string } | null
  onClose: () => void
  onSuccess?: () => void
}

/** 비밀번호 초기화 모달 — 원생 목록·상세 공유. 동작은 StudentManagement 인라인 모달과 동일. */
export function ResetPasswordModal({ target, onClose, onSuccess }: Props) {
  const { t } = useTranslation(['admin', 'common'])
  const queryClient = useQueryClient()
  const [password, setPassword] = useState('s1234')

  useEffect(() => { if (target) setPassword('s1234') }, [target])

  const mutation = useMutation({
    mutationFn: ({ userId, newPassword }: { userId: number; newPassword?: string }) =>
      memberApi.resetPassword(userId, newPassword),
    onSuccess: (response) => {
      const tempPassword = response.data?.data?.temporaryPassword
      alert(t('admin:students.toast.resetPasswordSuccess', { password: tempPassword }))
      onClose()
      onSuccess?.()
      queryClient.invalidateQueries({ queryKey: ['members'] })
    },
    onError: (error: any) => {
      console.error('Password reset failed:', error)
      let errorMessage = t('admin:students.toast.resetPasswordFailed')
      if (error?.response?.data?.error) {
        const errorObj = error.response.data.error
        if (typeof errorObj === 'string') errorMessage = errorObj
        else if (errorObj?.message) errorMessage = errorObj.message
      }
      alert(t('admin:students.toast.errorPrefix', { message: errorMessage }))
    },
  })

  return (
    <Dialog open={!!target} onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="sm:max-w-md p-0 gap-0 grid-rows-[auto_minmax(0,1fr)_auto]">
        <DialogHeader className="px-5 py-4 border-b pr-10">
          <DialogTitle>{t('admin:students.resetPassword.title')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 px-5 py-4 overflow-y-auto">
          <p className="text-sm text-gray-600">
            <Trans
              i18nKey="students.resetPassword.description"
              ns="admin"
              values={{ name: target?.fullName ?? '' }}
              components={{ strong: <strong /> }}
            />
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin:students.resetPassword.newPassword')}</label>
            <input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="s1234"
            />
            <p className="mt-1 text-xs text-gray-500">{t('admin:students.resetPassword.helpText')}</p>
          </div>
          <div className="flex items-start space-x-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">{t('admin:students.resetPassword.warning')}</p>
          </div>
        </div>
        <DialogFooter className="mx-0 mb-0 px-5">
          <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
            {t('common:button.cancel')}
          </button>
          <button
            type="button"
            onClick={() => { if (target) mutation.mutate({ userId: target.userId, newPassword: password.trim() || undefined }) }}
            disabled={mutation.isPending}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {mutation.isPending ? t('admin:students.resetPassword.submitting') : t('admin:students.resetPassword.submit')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
